
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { OrderFormValues } from '../orderFormSchema';
import { checkSubdomainAvailability } from '@/utils/instanceService';

interface SubdomainFieldProps {
  form: UseFormReturn<OrderFormValues>;
}

const SubdomainField: React.FC<SubdomainFieldProps> = ({ form }) => {
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);
  const [isSubdomainAvailable, setIsSubdomainAvailable] = useState<boolean | null>(null);
  
  const checkSubdomain = async (subdomain: string) => {
    if (subdomain.length < 3) return;
    
    setIsCheckingSubdomain(true);
    try {
      const isAvailable = await checkSubdomainAvailability(subdomain);
      setIsSubdomainAvailable(isAvailable);
    } catch (error) {
      console.error('Error checking subdomain:', error);
      setIsSubdomainAvailable(null);
    } finally {
      setIsCheckingSubdomain(false);
    }
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Subdomain Setup</h3>
      <FormField
        control={form.control}
        name="desiredSubdomain"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Desired Subdomain</FormLabel>
            <div className="flex items-center space-x-2">
              <FormControl>
                <Input 
                  {...field} 
                  placeholder="your-business"
                  onChange={(e) => {
                    field.onChange(e);
                    if (e.target.value.length >= 3) {
                      checkSubdomain(e.target.value);
                    } else {
                      setIsSubdomainAvailable(null);
                    }
                  }}
                  className={isSubdomainAvailable === true 
                    ? "border-green-500 focus:border-green-500 focus:ring-green-500" 
                    : isSubdomainAvailable === false
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : ""}
                />
              </FormControl>
              <span className="text-gray-500">.referme.site</span>
            </div>
            {isCheckingSubdomain && (
              <div className="text-sm text-gray-500 mt-1 flex items-center">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary mr-2"></div>
                Checking availability...
              </div>
            )}
            {isSubdomainAvailable === true && !isCheckingSubdomain && field.value && (
              <div className="text-sm text-green-600 mt-1">
                Subdomain is available!
              </div>
            )}
            {isSubdomainAvailable === false && !isCheckingSubdomain && field.value && (
              <div className="text-sm text-red-600 mt-1">
                Subdomain is already taken. Please try another.
              </div>
            )}
            <FormDescription>
              This will be the web address for resellers. The customer access domain will be set up in your login later.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default SubdomainField;
